import React, { useState } from 'react';
import { Mail, Phone, MapPin, TrendingUp, Users, BookOpen, Building2, Handshake, ArrowRight, Megaphone, Target, Sparkles, Eye, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import VisitorNav from '../components/VisitorNav';
import VisitorTopMenuBar from '../components/VisitorTopMenuBar';
import VisitorFooter from '../components/VisitorFooter';
import VisitorMobileBottomNav from '../components/VisitorMobileBottomNav';
import ReaderNav from '../components/ReaderNav';
import ReaderTopMenuBar from '../components/ReaderTopMenuBar';
import ReaderMobileBottomNav from '../components/ReaderMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Seo, PAGE_SEO } from '../components/Seo';

const ContactPage = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showAdvertiserModal, setShowAdvertiserModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage(data.message || 'Your message has been sent successfully! We\'ll be in touch soon.');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitMessage(data.detail || 'Failed to send message. Please try again or email us directly at info@newaudia.com');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage('Failed to send message. Please try again or email us directly at info@newaudia.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white pb-20 md:pb-0 overflow-x-hidden">
      <Seo {...PAGE_SEO.contact} />
      {isAuthenticated ? (
        <>
          <ReaderNav />
          <ReaderTopMenuBar />
        </>
      ) : (
        <>
          <VisitorNav />
          <VisitorTopMenuBar />
        </>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4zIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]"></div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="max-w-4xl mx-auto text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Contact NewAudia™
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl">
              We'd love to hear from you. Whether you're an author, listener, partner, or investor, reach out anytime. NewAudia™ exists to elevate storytellers and bring smart-audio publishing into the future—let's build it together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            
            {/* General Inquiries */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 backdrop-blur-sm">
              <h2 className="text-xl font-bold text-white mb-4">General Inquiries</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Email:</p>
                    <a 
                      href="mailto:info@newaudia.com" 
                      className="text-white hover:text-purple-400 transition-colors"
                    >
                      info@newaudia.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Phone:</p>
                    <a 
                      href="tel:+17192056381" 
                      className="text-white hover:text-purple-400 transition-colors"
                    >
                      (719) 205-6381
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Investors & Partners */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 backdrop-blur-sm">
              <h2 className="text-xl font-bold text-white mb-4">Investors & Partners</h2>
              <p className="text-sm text-slate-300 mb-4">
                If you're interested in supporting the future of adaptive smart-audio, we'd love to start a conversation.
              </p>
              <div className="flex items-start gap-3 mb-4">
                <Mail className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-slate-400 mb-1">Email:</p>
                  <a 
                    href="mailto:Partners@NewAudia.com" 
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Partners@NewAudia.com
                  </a>
                </div>
              </div>
              <Button
                onClick={() => setShowInvestorModal(true)}
                variant="outline"
                className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/20 hover:border-purple-400 text-sm"
                data-testid="investor-welcome-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Investor & Partner Welcome
              </Button>
            </div>

            {/* Advertise With Us */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 backdrop-blur-sm">
              <h2 className="text-xl font-bold text-white mb-4">Advertise With Us</h2>
              <p className="text-sm text-slate-300 mb-4">
                Reach engaged readers, authors, and publishers through strategic promotional partnerships.
              </p>
              <div className="flex items-start gap-3 mb-4">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-slate-400 mb-1">Email:</p>
                  <a 
                    href="mailto:Advertise@NewAudia.com" 
                    className="text-white hover:text-amber-400 transition-colors"
                  >
                    Advertise@NewAudia.com
                  </a>
                </div>
              </div>
              <Button
                onClick={() => setShowAdvertiserModal(true)}
                variant="outline"
                className="w-full border-amber-500/50 text-amber-300 hover:bg-amber-500/20 hover:border-amber-400 text-sm"
                data-testid="advertiser-welcome-btn"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Advertiser Welcome
              </Button>
            </div>

            {/* Location */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 backdrop-blur-sm">
              <h2 className="text-xl font-bold text-white mb-4">Location</h2>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white mb-2">Colorado Springs, CO</p>
                  <p className="text-sm text-slate-400 italic">(Meetings by appointment only)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Send us a message
              </h2>
              <p className="text-slate-300">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            {submitMessage && (
              <div className="mb-6 bg-green-900/30 border border-green-700 text-green-300 px-6 py-4 rounded-lg">
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* First Name & Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-2">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    required
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-2">
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
                    Telephone Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company (optional)"
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-300 mb-2">
                  Subject <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  required
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your question or comment..."
                  required
                  rows="6"
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-12 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {isAuthenticated ? (
        <UniversalFooter />
      ) : (
        <>
          <VisitorFooter />
          <VisitorMobileBottomNav />
        </>
      )}

      {/* Investor & Strategic Partner Welcome Modal */}
      <Dialog open={showInvestorModal} onOpenChange={setShowInvestorModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 border-indigo-500/30 text-slate-100">
          <DialogHeader className="border-b border-indigo-500/20 pb-4">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <Handshake className="w-6 h-6 text-indigo-400" />
              </div>
              Investor & Strategic Partner Welcome
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4 text-slate-300 text-sm leading-relaxed">
            {/* Welcome Statement */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Welcome to NewAudia.</h3>
              <p className="mb-3">
                NewAudia is being developed as a next-generation, reader-centric audio and interactive content platform designed to reimagine how stories are discovered, consumed, and engaged with. While the platform is already operational and publicly accessible, we view this phase as the foundation for a much broader and more ambitious ecosystem.
              </p>
              <p>
                NewAudia is currently operated as a business name (&quot;dba&quot;) of Use What You Got Publishing, LLC. As we continue to mature the platform, our focus extends beyond product development alone to include <strong className="text-white">strategic collaboration</strong> with aligned partners who recognize both the technological and cultural implications of what we are building.
              </p>
            </div>

            {/* Partnership Outlook */}
            <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Our Partnership Outlook</h3>
              <p className="mb-4">We are actively interested in conversations with:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-indigo-400" />
                  </div>
                  <p><strong className="text-white">Developers and technical partners</strong> seeking to extend, integrate, or enhance platform capabilities</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4 h-4 text-indigo-400" />
                  </div>
                  <p><strong className="text-white">Publishers, content organizations, and rights holders</strong> exploring new distribution, monetization, and engagement models</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-indigo-400" />
                  </div>
                  <p><strong className="text-white">Educational, cultural, and institutional partners</strong> aligned with long-term knowledge preservation and storytelling initiatives</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-indigo-400" />
                  </div>
                  <p><strong className="text-white">Strategic investors</strong> who value durable platforms, disciplined iteration, and long-horizon thinking over short-term speculation</p>
                </div>
              </div>
              <p className="mt-4 text-slate-400 italic">
                Our objective is not growth for its own sake, but <strong className="text-white">intentional expansion</strong>—guided by partners who bring expertise, perspective, and shared commitment to building a resilient and scalable system.
              </p>
            </div>

            {/* Current Phase */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Current Phase, Long-Term Vision</h3>
              <p className="mb-3">
                NewAudia is intentionally being developed through a staged, real-world deployment model. This allows us to validate assumptions, observe authentic user behavior, and refine both technology and governance before broader institutional scaling.
              </p>
              <p className="mb-3">We believe this approach:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-slate-400">
                <li>Reduces execution risk</li>
                <li>Improves long-term capital efficiency</li>
                <li>Produces stronger, more defensible platforms</li>
              </ul>
              <p className="mt-3 text-indigo-300">
                Partnerships formed at this stage have the opportunity to help shape not just features, but foundational direction.
              </p>
            </div>

            {/* Engaging With Us */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Engaging With Us</h3>
              <p className="mb-4">
                If you represent an organization, development group, institution, or investment entity interested in exploring a strategic relationship with NewAudia, we invite you to connect with us.
              </p>
              <p className="text-slate-400 italic">
                All inquiries are reviewed directly, and conversations are approached with discretion, clarity, and mutual respect.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <Mail className="w-5 h-5 text-indigo-400" />
                <a 
                  href="mailto:Partners@NewAudia.com" 
                  className="text-indigo-300 hover:text-indigo-200 transition-colors font-medium"
                >
                  Partners@NewAudia.com
                </a>
              </div>
            </div>

            {/* Entity Continuity Notice */}
            <div className="border-t border-slate-700 pt-4">
              <h4 className="text-sm font-semibold text-white mb-2">Entity Continuity Notice</h4>
              <p className="text-xs text-slate-400">
                Operational responsibility for NewAudia may transition in the future to a successor entity, including <strong className="text-slate-300">NewAudia Systems, LLC</strong>, without disruption to platform operations or partner relationships.
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-indigo-500/20">
            <Button
              onClick={() => setShowInvestorModal(false)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advertiser & Promotional Partner Modal */}
      <Dialog open={showAdvertiserModal} onOpenChange={setShowAdvertiserModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950 border-amber-500/30 text-slate-100">
          <DialogHeader className="border-b border-amber-500/20 pb-4">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-amber-400" />
              </div>
              Advertise with NewAudia
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4 text-slate-300 text-sm leading-relaxed">
            {/* Welcome Statement */}
            <div>
              <p className="mb-3">
                <strong className="text-white">Welcome, and thank you for your interest in NewAudia.</strong>
              </p>
              <p className="mb-3">
                NewAudia is an emerging, reader-centric platform designed to reshape how people discover, consume, and interact with books, audiobooks, authors, and ideas. Our ecosystem brings together <strong className="text-white">readers, independent authors, publishers, and creators</strong> in a highly engaged, content-driven environment.
              </p>
              <p>
                While our advertising and promotional infrastructure is currently in its early stages, we recognize the value of <strong className="text-white">strategic partnerships</strong> and <strong className="text-white">thoughtful promotion</strong> that aligns with our audience and mission.
              </p>
            </div>

            {/* Why Advertise */}
            <div className="bg-amber-900/20 border border-amber-500/20 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Why Advertise with NewAudia?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">1. Highly Engaged Audiences</h4>
                    <p className="text-slate-400">NewAudia users are not passive scrollers. They are active listeners, readers, learners, and thinkers—engaging deeply with long-form content, ideas, and conversations. This creates a unique opportunity for <strong className="text-slate-200">high-signal, low-noise exposure</strong>.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">2. Cross-Audience Reach</h4>
                    <p className="text-slate-400 mb-2">Our platform attracts:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-slate-400">
                      <li>Readers and audiobook listeners</li>
                      <li>Independent authors and creators</li>
                      <li>Publishers and content producers</li>
                      <li>Educators, technologists, and lifelong learners</li>
                    </ul>
                    <p className="mt-2 text-slate-400">This allows advertisers to reach <strong className="text-slate-200">multiple complementary audiences</strong> through a single platform.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">3. Contextual Relevance</h4>
                    <p className="text-slate-400">NewAudia emphasizes content relevance and intentional discovery. Promotional placements are designed to be <strong className="text-slate-200">context-aware</strong>, respectful of the user experience, and aligned with meaningful content—not disruptive or intrusive.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">4. Early Partner Advantage</h4>
                    <p className="text-slate-400 mb-2">As an early-stage platform, NewAudia offers select partners the opportunity to:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 text-slate-400">
                      <li>Shape future advertising formats</li>
                      <li>Influence placement strategy</li>
                      <li>Participate in pilot programs and preferred positioning</li>
                      <li>Build brand familiarity as the platform scales</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Capabilities */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Current Advertising Capabilities</h3>
              <p className="mb-3">
                At this time, NewAudia supports <strong className="text-white">limited, selectively placed promotional placements</strong>, such as:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-slate-400 mb-3">
                <li>Static or rotating banner placements</li>
                <li>Sponsored informational surfaces</li>
                <li>Early pilot visibility opportunities (by approval)</li>
              </ul>
              <p className="text-slate-500 italic text-xs">
                We are not yet offering programmatic advertising, real-time bidding, or automated ad placement systems.
              </p>
            </div>

            {/* What We're Looking For */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">What We&apos;re Looking For</h3>
              <p className="mb-3">We are especially interested in hearing from organizations and individuals aligned with:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Books, publishing, and media</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Education and learning platforms</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Creative tools and creator services</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Technology and AI brands</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Values-driven products</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300">Innovation-focused services</span>
                </div>
              </div>
              <p className="mt-4 text-slate-400 italic text-sm">
                All inquiries are reviewed manually to ensure alignment with the NewAudia experience.
              </p>
            </div>

            {/* Call to Action */}
            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-5 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">Interested in advertising or promoting with NewAudia?</h3>
              <p className="text-slate-300 mb-4">We&apos;d like to hear from you.</p>
              <a 
                href="mailto:Advertise@NewAudia.com"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                <Mail className="w-5 h-5" />
                Contact us to start the conversation
              </a>
              <p className="mt-3 text-xs text-slate-500">
                Advertising inquiries are reviewed on a rolling basis.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="border-t border-slate-700 pt-4">
              <p className="text-xs text-slate-500">
                Advertising availability is limited and subject to review. Submission of an inquiry does not guarantee placement or partnership. NewAudia reserves the right to approve or decline promotional opportunities to protect the integrity of the platform and user experience.
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-amber-500/20">
            <Button
              onClick={() => setShowAdvertiserModal(false)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactPage;
